import { useState } from 'react';
import { Button, Tooltip } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { useParams } from 'react-router';
import { Aks2talOnly } from 'components/AdminOnly';
import Message, { Type } from 'components/Notification/Message';
import confirmModal from 'general/confirmModal';
import { useAppSelector } from 'hooks';
import useIsReadonlyPath from 'hooks/useIsReadonlyPath';
import { saveAuditTemplateAsPreset } from '../lib/auditTemplatePresets';

const messageKey = 'SaveToPreset';

export default function SaveToPreset() {
  const isReadOnly = useIsReadonlyPath();
  const { orgId, auditTemplateId } = useParams<{
    orgId: string;
    auditTemplateId: string;
  }>();
  const [saving, setSaving] = useState(false);
  const currentAuditTemplate = useAppSelector((state) => state.auditTemplates.currentAuditTemplate);
  const currentAuditTemplateTasks = useAppSelector(
    (state) => state.auditTemplates.currentAuditTemplateTasks
  );
  const isUpdatingPreset = Boolean(currentAuditTemplate.sourcePresetId);
  const buttonText = isUpdatingPreset ? 'Opdatér standardskabelon' : 'Gem som standardskabelon';

  const handleSave = async () => {
    try {
      if (!orgId || !auditTemplateId) {
        throw new Error('orgId or auditTemplateId is undefined');
      }

      if (currentAuditTemplate.id !== auditTemplateId) {
        throw new Error('currentAuditTemplate.id !== auditTemplateId');
      }

      if (!currentAuditTemplate.title?.trim()) {
        throw new Error('Template title is missing');
      }

      setSaving(true);
      Message({
        key: messageKey,
        type: Type.LOADING,
        message: 'Gemmer standardskabelon',
      });

      await saveAuditTemplateAsPreset({
        orgId,
        template: currentAuditTemplate,
        tasks: currentAuditTemplateTasks,
      });

      Message({
        key: messageKey,
        type: Type.SUCCESS,
        message: 'Standardskabelonen blev gemt',
      });
    } catch (error) {
      console.error(error);
      Message({
        key: messageKey,
        type: Type.ERROR,
        message: 'Standardskabelonen kunne ikke gemmes',
      });
    } finally {
      setSaving(false);
    }
  };

  const onConfirm = () => {
    confirmModal({
      title: buttonText,
      content: isUpdatingPreset
        ? 'Den centrale standardskabelon bliver opdateret med denne skabelons nuværende titel, note og opgaver.'
        : 'Denne skabelons titel, note og opgaver bliver gemt som en ny central standardskabelon.',
      onOk: () => {
        void handleSave();
      },
    });
  };

  return (
    <Aks2talOnly>
      <Tooltip placement="left" title="Gem denne skabelon i Aks2tal standardskabeloner">
        <Button
          disabled={isReadOnly || saving}
          loading={saving}
          icon={<SaveOutlined />}
          block
          onClick={onConfirm}
          type="default"
        >
          {buttonText}
        </Button>
      </Tooltip>
    </Aks2talOnly>
  );
}
